<template>
  <div>
    <Header />

    <!-- Hero Section -->
    <section class="relative text-white pt-16 h-80 flex items-center">
      <!-- Background -->
      <div class="absolute inset-0 hero-gradient"></div>
      <div class="absolute inset-0 bg-black bg-opacity-30"></div>

      <!-- Content -->
      <div
        class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <div class="flex items-center justify-center mb-4">
          <div class="bg-white bg-opacity-20 p-4 rounded-full mr-4">
            <svg
              class="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              ></path>
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
            </svg>
          </div>
          <h1 class="text-4xl md:text-5xl font-bold animate-fade-in-up">
            Politique des Cookies
          </h1>
        </div>
        <p
          class="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto animate-fade-in-up"
        >
          Gestion et utilisation des cookies sur notre site
        </p>
      </div>
    </section>

    <!-- Breadcrumb -->
    <section class="bg-white py-4 border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-4">
            <li>
              <div>
                <a href="/" class="text-gray-400 hover:text-gray-500">
                  <svg
                    class="flex-shrink-0 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                    />
                  </svg>
                  <span class="sr-only">Accueil</span>
                </a>
              </div>
            </li>
            <li>
              <div class="flex items-center">
                <svg
                  class="flex-shrink-0 h-5 w-5 text-gray-300"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="ml-4 text-sm font-medium text-gray-500"
                  >Politique des Cookies</span
                >
              </div>
            </li>
          </ol>
        </nav>
      </div>
    </section>

    <!-- Introduction -->
    <section class="py-16 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 mb-12"
        >
          <div class="flex items-start space-x-4">
            <div class="bg-orange-500 p-3 rounded-full">
              <svg
                class="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div>
              <h2 class="text-2xl font-bold text-gray-900 mb-4">
                Qu'est-ce qu'un Cookie ?
              </h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                Un cookie est un petit fichier texte stocké sur votre appareil
                (ordinateur, tablette, smartphone) lorsque vous visitez notre
                site web. Les cookies nous permettent d'améliorer votre
                expérience de navigation et de vous offrir des services
                personnalisés.
              </p>
              <div class="bg-white p-4 rounded-lg border border-orange-200">
                <h3 class="font-semibold text-gray-900 mb-2">
                  🍪 Pourquoi utilisons-nous des cookies ?
                </h3>
                <ul class="text-sm text-gray-600 space-y-1">
                  <li>• Assurer le bon fonctionnement du site</li>
                  <li>• Mémoriser vos préférences de navigation</li>
                  <li>• Analyser l'utilisation du site pour l'améliorer</li>
                  <li>• Vous offrir une expérience personnalisée</li>
                </ul>
              </div>
              <p class="text-sm text-gray-500 mt-4">
                <strong>Dernière mise à jour :</strong> 10 septembre 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Types de cookies -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            Types de Cookies Utilisés
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Nous utilisons différents types de cookies selon leur fonction et
            leur durée de vie
          </p>
        </div>

        <div class="grid lg:grid-cols-3 gap-8">
          <!-- Cookies techniques -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-center mb-6">
              <div class="bg-green-100 p-3 rounded-full mr-4">
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <div>
                <span
                  class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold"
                  >NÉCESSAIRES</span
                >
                <h3 class="text-xl font-bold text-gray-900 mt-2">
                  Cookies Techniques
                </h3>
              </div>
            </div>

            <div class="space-y-4">
              <p class="text-gray-600">
                Indispensables au fonctionnement du site. Ils ne peuvent pas
                être désactivés.
              </p>

              <div class="bg-green-50 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 mb-2">Exemples :</h4>
                <ul class="text-sm text-gray-600 space-y-1">
                  <li>• Authentification et sécurité</li>
                  <li>• Panier d'achat (si applicable)</li>
                  <li>• Préférences de langue</li>
                  <li>• Navigation et accessibilité</li>
                </ul>
              </div>

              <div
                class="flex items-center justify-between p-3 bg-gray-100 rounded-lg"
              >
                <span class="text-sm font-medium text-gray-700">Statut</span>
                <span class="text-sm text-green-600 font-semibold"
                  >Toujours actifs</span
                >
              </div>
            </div>
          </div>

          <!-- Cookies fonctionnels -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-center mb-6">
              <div class="bg-blue-100 p-3 rounded-full mr-4">
                <svg
                  class="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
              </div>
              <div>
                <span
                  class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold"
                  >FONCTIONNELS</span
                >
                <h3 class="text-xl font-bold text-gray-900 mt-2">
                  Cookies de Préférences
                </h3>
              </div>
            </div>

            <div class="space-y-4">
              <p class="text-gray-600">
                Améliorent votre expérience en mémorisant vos choix et
                préférences.
              </p>

              <div class="bg-blue-50 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 mb-2">Exemples :</h4>
                <ul class="text-sm text-gray-600 space-y-1">
                  <li>• Préférences d'affichage</li>
                  <li>• Choix de langue</li>
                  <li>• Paramètres d'accessibilité</li>
                  <li>• Contenu personnalisé</li>
                </ul>
              </div>

              <div
                class="flex items-center justify-between p-3 bg-gray-100 rounded-lg"
              >
                <span class="text-sm font-medium text-gray-700">Contrôle</span>
                <label class="toggle-switch">
                  <input type="checkbox" id="functional-cookies" checked />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Cookies analytiques -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-center mb-6">
              <div class="bg-purple-100 p-3 rounded-full mr-4">
                <svg
                  class="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  ></path>
                </svg>
              </div>
              <div>
                <span
                  class="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-semibold"
                  >ANALYTIQUES</span
                >
                <h3 class="text-xl font-bold text-gray-900 mt-2">
                  Cookies Statistiques
                </h3>
              </div>
            </div>

            <div class="space-y-4">
              <p class="text-gray-600">
                Nous aident à comprendre comment vous utilisez notre site
                (données anonymisées).
              </p>

              <div class="bg-purple-50 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 mb-2">Exemples :</h4>
                <ul class="text-sm text-gray-600 space-y-1">
                  <li>• Pages les plus visitées</li>
                  <li>• Temps passé sur le site</li>
                  <li>• Parcours de navigation</li>
                  <li>• Erreurs rencontrées</li>
                </ul>
              </div>

              <div
                class="flex items-center justify-between p-3 bg-gray-100 rounded-lg"
              >
                <span class="text-sm font-medium text-gray-700">Contrôle</span>
                <label class="toggle-switch">
                  <input type="checkbox" id="analytics-cookies" checked />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Panneau de gestion des cookies -->
    <section class="py-16 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-2xl p-8"
        >
          <div class="text-center mb-8">
            <h2 class="text-2xl font-bold mb-4">
              🍪 Panneau de Gestion des Cookies
            </h2>
            <p class="opacity-90">
              Personnalisez vos préférences de cookies selon vos besoins
            </p>
          </div>

          <div class="space-y-6">
            <!-- Cookies techniques -->
            <div class="bg-white bg-opacity-10 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold">Cookies Techniques</h3>
                  <p class="text-sm opacity-80">
                    Nécessaires au fonctionnement du site
                  </p>
                </div>
                <div class="flex items-center">
                  <span class="text-sm mr-3">Toujours actifs</span>
                  <div
                    class="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1"
                  >
                    <div class="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
              <p class="text-sm opacity-75">
                Ces cookies sont indispensables et ne peuvent pas être
                désactivés. Ils incluent l'authentification, la sécurité et les
                fonctions de base du site.
              </p>
            </div>

            <!-- Cookies fonctionnels -->
            <div class="bg-white bg-opacity-10 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold">Cookies Fonctionnels</h3>
                  <p class="text-sm opacity-80">Améliorent votre expérience</p>
                </div>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    id="functional-cookies-panel"
                    checked
                  />
                  <span class="slider"></span>
                </label>
              </div>
              <p class="text-sm opacity-75">
                Ces cookies mémorisent vos préférences et choix pour vous offrir
                une expérience personnalisée lors de vos prochaines visites.
              </p>
            </div>

            <!-- Cookies analytiques -->
            <div class="bg-white bg-opacity-10 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold">Cookies Analytiques</h3>
                  <p class="text-sm opacity-80">
                    Nous aident à améliorer le site
                  </p>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" id="analytics-cookies-panel" checked />
                  <span class="slider"></span>
                </label>
              </div>
              <p class="text-sm opacity-75">
                Ces cookies collectent des informations anonymes sur
                l'utilisation du site pour nous aider à l'améliorer et à mieux
                comprendre vos besoins.
              </p>
            </div>
          </div>

          <div
            class="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-8"
          >
            <button
              onclick="savePreferences()"
              class="bg-white text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex-1"
            >
              💾 Sauvegarder mes préférences
            </button>
            <button
              onclick="acceptAll()"
              class="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex-1"
            >
              ✅ Tout accepter
            </button>
            <button
              onclick="rejectAll()"
              class="border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-slate-700 transition-colors flex-1"
            >
              ❌ Tout refuser
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Gestion par navigateur -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            Gestion des Cookies par Navigateur
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Vous pouvez également gérer les cookies directement depuis les
            paramètres de votre navigateur
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Chrome -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 section-card text-center"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-blue-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6 0 5.302-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Google Chrome</h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p><strong>1.</strong> Menu ⋮ > Paramètres</p>
              <p><strong>2.</strong> Confidentialité et sécurité</p>
              <p><strong>3.</strong> Cookies et autres données</p>
              <p><strong>4.</strong> Gérer les cookies</p>
            </div>
            <button
              onclick="showChromeGuide()"
              class="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              Guide détaillé
            </button>
          </div>

          <!-- Firefox -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 section-card text-center"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-orange-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6 0 5.302-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">
              Mozilla Firefox
            </h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p><strong>1.</strong> Menu ☰ > Paramètres</p>
              <p><strong>2.</strong> Vie privée et sécurité</p>
              <p><strong>3.</strong> Cookies et données</p>
              <p><strong>4.</strong> Gérer les données</p>
            </div>
            <button
              onclick="showFirefoxGuide()"
              class="mt-4 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors"
            >
              Guide détaillé
            </button>
          </div>

          <!-- Safari -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 section-card text-center"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-gray-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6 0 5.302-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Safari</h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p><strong>1.</strong> Safari > Préférences</p>
              <p><strong>2.</strong> Onglet Confidentialité</p>
              <p><strong>3.</strong> Cookies et données</p>
              <p><strong>4.</strong> Gérer les données</p>
            </div>
            <button
              onclick="showSafariGuide()"
              class="mt-4 bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors"
            >
              Guide détaillé
            </button>
          </div>

          <!-- Edge -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 section-card text-center"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-indigo-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6 0 5.302-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Microsoft Edge</h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p><strong>1.</strong> Menu ⋯ > Paramètres</p>
              <p><strong>2.</strong> Confidentialité et services</p>
              <p><strong>3.</strong> Cookies et données</p>
              <p><strong>4.</strong> Gérer les cookies</p>
            </div>
            <button
              onclick="showEdgeGuide()"
              class="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors"
            >
              Guide détaillé
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Vos droits -->
    <section class="py-16 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
          <div class="flex items-start space-x-4">
            <div class="bg-green-500 p-3 rounded-full">
              <svg
                class="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                ></path>
              </svg>
            </div>
            <div class="flex-1">
              <h2 class="text-2xl font-bold text-gray-900 mb-4">
                Vos Droits Concernant les Cookies
              </h2>
              <p class="text-gray-600 leading-relaxed mb-6">
                Conformément à la réglementation en vigueur, vous disposez de
                plusieurs droits concernant l'utilisation des cookies sur notre
                site.
              </p>

              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-white p-6 rounded-lg border border-green-200">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    ✅ Vos Droits
                  </h3>
                  <ul class="space-y-2 text-sm text-gray-600">
                    <li>• Consentir ou refuser les cookies non-essentiels</li>
                    <li>• Modifier vos préférences à tout moment</li>
                    <li>• Être informé de l'utilisation des cookies</li>
                    <li>• Supprimer les cookies existants</li>
                    <li>• Accéder aux informations sur les cookies</li>
                  </ul>
                </div>

                <div class="bg-white p-6 rounded-lg border border-blue-200">
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    🔧 Comment Exercer vos Droits
                  </h3>
                  <ul class="space-y-2 text-sm text-gray-600">
                    <li>• Utilisez notre panneau de gestion</li>
                    <li>• Configurez votre navigateur</li>
                    <li>• Contactez-nous directement</li>
                    <li>• Consultez notre politique de confidentialité</li>
                    <li>• Utilisez les outils de votre navigateur</li>
                  </ul>
                </div>
              </div>

              <div
                class="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div class="flex items-center mb-2">
                  <svg
                    class="w-5 h-5 text-amber-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    ></path>
                  </svg>
                  <h4 class="font-semibold text-amber-800">Important</h4>
                </div>
                <p class="text-sm text-amber-700">
                  Le refus de certains cookies peut affecter le fonctionnement
                  du site et votre expérience de navigation. Les cookies
                  techniques restent nécessaires au bon fonctionnement du site.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Cookie Banner -->
    <div id="cookie-banner" class="cookie-banner">
      <div
        class="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0 lg:space-x-6"
      >
        <div class="flex items-center space-x-4">
          <div class="bg-white bg-opacity-20 p-2 rounded-full">
            <svg
              class="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              ></path>
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
            </svg>
          </div>
          <div>
            <p class="font-semibold">🍪 Gestion des Cookies</p>
            <p class="text-sm opacity-90">
              Nous utilisons des cookies pour améliorer votre expérience. Vous
              pouvez personnaliser vos préférences.
            </p>
          </div>
        </div>
        <div
          class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3"
        >
          <button
            onclick="showCookieSettings()"
            class="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-30 transition-colors"
          >
            ⚙️ Personnaliser
          </button>
          <button
            onclick="acceptAllCookies()"
            class="bg-white text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            ✅ Tout accepter
          </button>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Header from "../components/Header.vue";
import Footer from "../components/Footer.vue";
export default {
  name: "Cookies",
  components: {
    Header,
    Footer,
  },
  data() {
    return {};
  },
};
</script>
<style scoped>
.hero-gradient {
  background: linear-gradient(135deg, #4f6bb6 0%, #5a67a8 100%);
}
.smooth-scroll {
  scroll-behavior: smooth;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 1s ease-out;
}

.section-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4f6bb6;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #4f6bb6 0%, #5a67a8 100%);
  color: white;
  padding: 1rem;
  z-index: 1000;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.cookie-banner.show {
  transform: translateY(0);
}
</style>
